// ai-chat/src/lib/chat-entity-data-card/chat-entity-data-card.component.ts
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';
import { RapiderListGridComponent } from '@rapider/list-grid';

import {
  AI_CHAT_CONFIG,
  separateRapiderMetadata,
  getExtractedMetaTitlePure,
  getCleansedEntityDataAtPure,
} from '../ai-chat.config';

/**
 * @class ChatEntityDataCardComponent
 * @description A standalone UI component rendering the payload of AI returned entities.
 * Includes capabilities to toggle between JSON List view and Grid View, and auto-collapses large arrays.
 */
@Component({
  selector: 'rapider-chat-entity-data-card',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzTooltipModule,
    RapiderJsonViewerComponent,
    RapiderListGridComponent,
  ],
  templateUrl: './chat-entity-data-card.component.html',
})
export class ChatEntityDataCardComponent implements OnChanges {
  @Input({ required: true }) entityName!: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input({ required: true }) data!: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() gridConfig: any = null;
  @Input() isGridLoading: boolean = false;

  @Output() showInCanvas = new EventEmitter<void>();

  public readonly config = AI_CHAT_CONFIG;
  public getExtractedMetaTitlePure = getExtractedMetaTitlePure;
  public getCleansedEntityDataAtPure = getCleansedEntityDataAtPure;

  // State Signals
  public showGrid = signal<boolean>(false);
  public showAll = signal<boolean>(false);

  // Data processing signals
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public cleansedData = signal<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public extractedMeta = signal<any>(null);

  /**
   * Lifecycle hook triggers whenever `@Input()` properties change.
   * Auto-switches to the grid view if the array contains more than 2 items.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      const processed = separateRapiderMetadata(this.data);
      this.cleansedData.set(processed.cleansed);
      this.extractedMeta.set(processed.extracted);

      // Requirement 1: Auto-switch to list-grid view if > 2 items
      if (Array.isArray(this.data) && this.data.length > 2) {
        this.showGrid.set(true);
      }
    }
  }

  /** Checks if the data payload is an array with multiple objects (more than 1) to enable the toggle icons. */
  public hasMultipleItems = computed(() => {
    return Array.isArray(this.data) && this.data.length > 1;
  });

  /** Returns an array of iterable indices for the template, slicing to a max of 2 if collapsed. */
  public displayIndices = computed(() => {
    if (!Array.isArray(this.data)) return [0];
    const maxItems = this.showAll() ? this.data.length : 2;
    return Array.from(
      { length: Math.min(this.data.length, maxItems) },
      (_, i) => i,
    );
  });

  /** Expands the list view to display all items instead of just the first 2. */
  public toggleShowAll(): void {
    this.showAll.set(true);
  }
}
